import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, 
  User,
  Phone,
  MapPin,
  CreditCard,
  Save,
  Mail,
  Percent,
  Building2,
  Smartphone
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Profile() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    phone: user?.phone || '',
    address: user?.address || '',
    iban: user?.iban || '',
    bank_name: user?.bank_name || '',
    revolut_account: user?.revolut_account || ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API_URL}/users/me`, formData);
      updateUser(response.data);
      toast.success(t('profile.updateSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile.updateError'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">{t('profile.title')}</h1>
          <p className="text-slate-400 text-sm">{t('profile.manageInfo')}</p>
        </div>
      </div>

      {/* User Info Card (Read Only) */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-blue-400" />
          {t('profile.accountInfo')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <User className="w-4 h-4" />
              <span className="text-sm">{t('common.name')}</span>
            </div>
            <p className="text-white font-medium">{user?.name}</p>
          </div>
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <Mail className="w-4 h-4" />
              <span className="text-sm">{t('common.email')}</span>
            </div>
            <p className="text-white font-medium">{user?.email}</p>
          </div>
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <Percent className="w-4 h-4" />
              <span className="text-sm">{t('dashboard.profitPercentage')}</span>
            </div>
            <p className="text-emerald-400 font-medium">{user?.profit_percentage}% {t('profile.monthly')}</p>
          </div>
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <CreditCard className="w-4 h-4" />
              <span className="text-sm">{t('profile.currentBalance')}</span>
            </div>
            <p className="text-white font-medium">{formatCurrency(user?.balance)}</p>
          </div>
        </div>
      </div>

      {/* Editable Form */}
      <form onSubmit={handleSubmit}>
        <div className="glass-card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white mb-4">{t('profile.editableData')}</h2>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-slate-300 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              {t('common.phone')}
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+351 912 345 678"
              className="h-12 bg-[#161C22] border-white/10 text-white rounded-xl focus:border-blue-500"
              data-testid="profile-phone"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address" className="text-slate-300 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {t('common.address')}
            </Label>
            <Input
              id="address"
              name="address"
              type="text"
              value={formData.address}
              onChange={handleChange}
              placeholder={t('common.address')}
              className="h-12 bg-[#161C22] border-white/10 text-white rounded-xl focus:border-blue-500"
              data-testid="profile-address"
            />
          </div>

          {/* Bank Details Section */}
          <div className="pt-4 border-t border-white/10">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              {t('profile.bankDetails')}
            </h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bank_name" className="text-slate-300">
                  {t('profile.bankName')}
                </Label>
                <Input
                  id="bank_name"
                  name="bank_name"
                  type="text"
                  value={formData.bank_name}
                  onChange={handleChange}
                  placeholder="Ex: Millennium BCP, Santander, CGD..."
                  className="h-12 bg-[#161C22] border-white/10 text-white rounded-xl focus:border-blue-500"
                  data-testid="profile-bank"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="iban" className="text-slate-300 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  {t('profile.iban')}
                </Label>
                <Input
                  id="iban"
                  name="iban"
                  type="text"
                  value={formData.iban}
                  onChange={handleChange}
                  placeholder="PT50 0000 0000 0000 0000 0000 0"
                  className="h-12 bg-[#161C22] border-white/10 text-white rounded-xl focus:border-blue-500 font-mono"
                  data-testid="profile-iban"
                />
              </div>
            </div>
          </div>

          {/* Revolut Section */}
          <div className="pt-4 border-t border-white/10">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-purple-400" />
              {t('profile.revolutOptional')}
            </h3>

            <div className="space-y-2">
              <Label htmlFor="revolut_account" className="text-slate-300">
                {t('profile.revolutUsernameTag')}
              </Label>
              <Input
                id="revolut_account"
                name="revolut_account"
                type="text"
                value={formData.revolut_account}
                onChange={handleChange}
                placeholder="@username"
                className="h-12 bg-[#161C22] border-white/10 text-white rounded-xl focus:border-blue-500"
                data-testid="profile-revolut"
              />
              <p className="text-xs text-slate-500">
                {t('profile.revolutHelp')}
              </p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all hover:scale-[1.02] disabled:opacity-50"
            data-testid="profile-submit"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('profile.saving')}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Save className="w-5 h-5" />
                {t('profile.saveChanges')}
              </div>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
